import type { Probe } from '../video/convert';
import { videoConvert } from '../video/engine';
import { COPY_BYTES_PER_SECOND, PREP_SECONDS_PER_SECOND } from '../video/plan';
import { deleteMedia, keepStorage, mediaErrorText, putMedia, spaceLeft } from '../video/media';
import { preparePhoto } from '../video/photo';
import {
  addClip, clipLength, CLIP_MIN_SECONDS, fillSlot, fmtTime, isEmptySlot, NAME_MAX_CHARS, newId, PHOTO_MAX_SECONDS, timeLeft,
  VIDEO_MAX_SECONDS, type VideoClip
} from '../video/project';
import { shortText } from '../print/rules';
import { $ } from './dom';
import { closeSheets, isOpen, openSheet } from './sheets';
import { toast } from './toast';
import type { VideoCtx } from './videoView';

/*
 * Bringing a clip in (choice ก+, boards 20–21 of the design canvas):
 *   1. pick a video file from the phone
 *   2. "เลือกช่วงที่จะใช้": drag the yellow frame over the small pictures to choose the part to keep
 *   3. that part is shrunk to 1080p and kept on this device, one clip at a time ("กำลังเตรียมคลิป"),
 *      then added to the end of the timeline. Each failure stays in the list with its reason and "ลองอีกครั้ง".
 *
 * เทมเพลตวิดีโอ: a job can go into a slot instead of the end (`slot`): the part is the slot's length (the frame in
 * step 2 keeps that length and only moves), or the whole clip when it is shorter (its last picture is then held).
 * fillSlots() takes many files at once for the empty slots in order, CapCut-style (each from its start).
 * A picture file becomes a still picture (src/video/photo.ts) for its slot.
 */

const STRIP_PICTURES = 8;
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const mb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1048576))} MB`;

interface Job {
  id: string;
  file: File; name: string; srcDur: number; a: number; b: number;
  /** goes into this slot (clip id) instead of the end of the timeline */
  slot: string | null;
  /** a still picture (no range, no encoding) */
  photo: boolean;
  /** from "many files at once": only into a slot that is still empty when its turn comes */
  mustBeEmpty: boolean;
  /** the work that was open when the part was chosen (saver.openedCount) */
  work: number;
  state: 'wait' | 'run' | 'done' | 'error';
  pct: number; error: string; thumb: string;
  abort: AbortController | null;
  row: {
    li: HTMLLIElement; img: HTMLElement; sub: HTMLElement; right: HTMLElement; bar: HTMLElement; err: HTMLElement; acts: HTMLElement;
    retry: HTMLElement; stopBtn: HTMLElement; remove: HTMLElement;
  } | null;
}

/** `otherWork`: a reason another video job (putting clips back) is running; one encoder at a time on a phone */
export function initVideoImport(ctx: VideoCtx, otherWork: () => string | null) {
  const fileIn = $<HTMLInputElement>('vFile');
  const strip = $('viStrip'), thumbs = $('viThumbs'), win = $('viWin'), hA = $('viHA'), hB = $('viHB');
  const shadeA = $('viShadeA'), shadeB = $('viShadeB'), prev = $<HTMLVideoElement>('viVideo'), msg = $('viMsg');
  const chip = $<HTMLButtonElement>('vPrepChip'), list = $('vpList');

  /* ---------- 1. picking a file ---------- */
  $('vAdd').onclick = () => {
    if (ctx.readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อนจึงจะเพิ่มคลิปได้', true); return; }
    const other = otherWork();
    if (other) { toast(other, true); return; }
    const p = ctx.project(); if (!p) return;
    if (free() < CLIP_MIN_SECONDS) { toast(`วิดีโอยาวครบ ${VIDEO_MAX_SECONDS / 60} นาทีแล้ว (รวมคลิปที่กำลังเตรียม) ตัดคลิปเดิมให้สั้นลงก่อนจึงจะเพิ่มคลิปได้`, true); return; }
    fileIn.value = '';
    fileIn.click();
  };
  fileIn.onchange = () => { const f = fileIn.files?.[0]; if (f) void choose(f); };

  /** seconds still free, counting the clips that are waiting or being prepared */
  function free(): number {
    const p = ctx.project();
    if (!p) return 0;
    const pending = jobs.filter(j => !j.slot && (j.state === 'wait' || j.state === 'run')).reduce((n, j) => n + (j.b - j.a), 0);
    return Math.max(0, timeLeft(p) - pending);
  }

  /* ---------- 2. choosing the part ---------- */
  /** `slot`/`fixed`: choosing the part for a slot — the frame keeps the slot's length and only moves */
  let pick: { file: File; info: Probe; a: number; b: number; max: number; url: string; run: number; slot: string | null; fixed: number } | null = null;
  let pickRun = 0;

  async function choose(file: File, slot: { id: string; len: number } | null = null) {
    toast(`กำลังอ่านไฟล์ "${shortText(file.name, 40)}"…`);
    let info: Probe;
    try { info = await (await videoConvert()).probe(file, file.name); }
    catch (e) { toast(errText(e), true); return; }
    const p = ctx.project();
    if (!p) return;
    const max = slot ? info.duration : Math.min(info.duration, free());
    if (max < CLIP_MIN_SECONDS) { toast(`วิดีโอยาวครบ ${VIDEO_MAX_SECONDS / 60} นาทีแล้ว`, true); return; }
    closePick();
    const run = ++pickRun;
    const fixed = slot ? Math.min(slot.len, info.duration) : 0;
    pick = { file, info, a: 0, b: slot ? fixed : max, max, url: URL.createObjectURL(file), run, slot: slot?.id ?? null, fixed };
    $('viFile').textContent = `${file.name} · ${info.width} × ${info.height} · ${fmtTime(info.duration)}${info.hasAudio ? '' : ' · ไม่มีเสียง'}`;
    $('viPrevMsg').hidden = true;
    prev.src = pick.url;
    say('');
    thumbs.textContent = '';
    for (let k = 0; k < STRIP_PICTURES; k++) thumbs.append(document.createElement('i'));
    paint();
    openSheet('shVImport');
    void videoConvert().then(v => v.filmstrip(file, info.duration, STRIP_PICTURES, 112, (k, url) => {
      const img = document.createElement('img'); img.src = url; img.alt = '';
      thumbs.children[k]?.replaceWith(img);
    }, () => run !== pickRun)).catch(e => say('แสดงภาพเล็กของคลิปไม่ได้: ' + errText(e)));
    const room = await spaceLeft();
    const need = (pick?.b ?? 0) * COPY_BYTES_PER_SECOND;
    if (room !== null && run === pickRun && room < need * 1.5) {
      say(`พื้นที่เก็บข้อมูลของเว็บในเครื่องนี้เหลือราว ${mb(room)} อาจไม่พอสำหรับคลิปนี้ (ต้องใช้ราว ${mb(need)}) ลองเลือกช่วงให้สั้นลง`);
    }
  }
  prev.addEventListener('error', () => {
    if (!pick) return;
    $('viPrevMsg').textContent = 'ดูตัวอย่างคลิปนี้ในเบราว์เซอร์นี้ไม่ได้ ใช้ภาพเล็กด้านล่างเลือกช่วงแทน (การเตรียมคลิปยังทำได้)';
    $('viPrevMsg').hidden = false;
  });

  function say(text: string) { msg.textContent = text; msg.hidden = !text; }

  function closePick() {
    if (pick) { URL.revokeObjectURL(pick.url); pick = null; }
    pickRun++;
    prev.removeAttribute('src'); prev.load();
  }

  function paint() {
    if (!pick) return;
    const d = pick.info.duration;
    const pa = pick.a / d * 100, pb = pick.b / d * 100;
    hA.style.left = `${pa}%`; hB.style.left = `${pb}%`;
    win.style.left = `${pa}%`; win.style.width = `${pb - pa}%`;
    shadeA.style.left = '0'; shadeA.style.width = `${pa}%`;
    shadeB.style.left = `${pb}%`; shadeB.style.right = '0';
    $('viA').textContent = `เริ่ม ${fmtTime(pick.a)}`;
    $('viB').textContent = `จบ ${fmtTime(pick.b)}`;
    const len = pick.b - pick.a;
    const slotLen = pick.slot ? slotLength(pick.slot) : 0;
    $('viLen').textContent = `ใช้ ${len.toFixed(1)} วิ จาก ${d.toFixed(1)} วิ`
      + (pick.slot ? (slotLen > len + 0.05 ? ` · ช่องยาว ${slotLen.toFixed(1)} วิ คลิปสั้นกว่า ภาพสุดท้ายจะค้างอีก ${(slotLen - len).toFixed(1)} วิ` : ' · ยาวเท่าช่อง (เลื่อนกรอบเพื่อเลือกช่วง)') : '');
    const left = free();
    $('viEst').textContent = `เตรียมราว ${Math.max(1, Math.round(len * PREP_SECONDS_PER_SECOND))} วิ · ใช้พื้นที่ราว ${mb(len * COPY_BYTES_PER_SECOND)}`
      + (left < d ? ` · วิดีโอเหลือที่ว่างอีก ${fmtTime(left)}` : '');
    for (const [h, v] of [[hA, pick.a], [hB, pick.b]] as const) {
      h.setAttribute('aria-valuemax', d.toFixed(1)); h.setAttribute('aria-valuenow', v.toFixed(1)); h.setAttribute('aria-valuetext', fmtTime(v));
    }
  }

  /** move one end of the part; the part stays at least CLIP_MIN_SECONDS and at most the time still free */
  function setEnd(which: 'a' | 'b', t: number) {
    if (!pick) return;
    const d = pick.info.duration, left = Math.min(d, free());
    if (pick.slot) {   // a slot's part keeps the slot's length: either handle moves the whole frame
      const f = pick.fixed;
      pick.a = which === 'a' ? Math.min(Math.max(0, t), d - f) : Math.min(Math.max(0, t - f), d - f);
      pick.b = pick.a + f;
      paint();
      if (prev.readyState >= 1) prev.currentTime = Math.min(which === 'a' ? pick.a : pick.b, d - 0.05);
      return;
    }
    if (which === 'a') pick.a = Math.min(Math.max(0, t, pick.b - left), pick.b - CLIP_MIN_SECONDS);
    else pick.b = Math.max(Math.min(d, t, pick.a + left), pick.a + CLIP_MIN_SECONDS);
    paint();
    const at = which === 'a' ? pick.a : pick.b;
    if (prev.readyState >= 1) prev.currentTime = Math.min(at, d - 0.05);
  }

  for (const [h, which] of [[hA, 'a'], [hB, 'b']] as const) {
    h.addEventListener('pointerdown', e => {
      e.preventDefault();
      h.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        if (!pick) return;
        const r = strip.getBoundingClientRect();
        setEnd(which, (ev.clientX - r.left) / r.width * pick.info.duration);
      };
      const up = () => { h.removeEventListener('pointermove', move); h.removeEventListener('pointerup', up); h.removeEventListener('pointercancel', up); };
      h.addEventListener('pointermove', move);
      h.addEventListener('pointerup', up);
      h.addEventListener('pointercancel', up);
    });
    h.addEventListener('keydown', e => {
      if (!pick || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      e.preventDefault();
      const step = (e.shiftKey ? 1 : 0.1) * (e.key === 'ArrowLeft' ? -1 : 1);
      setEnd(which, (which === 'a' ? pick.a : pick.b) + step);
    });
  }
  // tapping the strip shows that moment in the preview
  strip.addEventListener('pointerdown', e => {
    if (!pick || e.target === hA || e.target === hB) return;
    const r = strip.getBoundingClientRect();
    if (prev.readyState >= 1) prev.currentTime = Math.min(pick.info.duration - 0.05, (e.clientX - r.left) / r.width * pick.info.duration);
  });

  $('viCancel').onclick = () => { closePick(); closeSheets(); };
  $('viUse').onclick = () => {
    if (!pick) return;
    const other = otherWork();
    if (other) { say(other); return; }
    const p = ctx.project();
    if (!p) return;
    if (!pick.slot && pick.b - pick.a > free() + 0.05) { say(`วิดีโอเหลือที่ว่างอีก ${fmtTime(free())} (รวมคลิปที่กำลังเตรียม) เลื่อนกรอบให้สั้นลง`); return; }
    const thumbEl = thumbs.querySelector('img');
    queue([{
      id: newId(), file: pick.file, name: pick.file.name, srcDur: pick.info.duration, a: pick.a, b: pick.b, work: ctx.saver.openedCount,
      slot: pick.slot, photo: false, mustBeEmpty: false, state: 'wait', pct: 0, error: '', thumb: thumbEl?.src ?? '', abort: null, row: null
    }]);
    closePick();
  };

  /** put jobs in the list and start preparing */
  function queue(add: Job[]) {
    jobs.forEach(j => { if (j.state === 'done') dropRow(j); });
    jobs = jobs.filter(j => j.state !== 'done').concat(add);
    renderList();
    openSheet('shVPrep');
    void keepStorage();
    void runQueue();
  }

  /* ---------- slots (เทมเพลตวิดีโอ) ---------- */
  const isPicture = (f: File) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(f.name);
  function slotLength(id: string): number {
    const c = ctx.project()?.clips.find(x => x.id === id);
    return c ? clipLength(c) : 0;
  }
  /**
   * a job for slot `id`: a picture as it is; a video from its start, as long as the slot (or all of it when shorter).
   * Made at once (no waiting), so the slots are taken before anything else can choose them; the video file is read
   * when its turn comes (runJob), and a file that cannot be read stays in the list with its reason.
   */
  function slotJob(file: File, id: string, work: number, mustBeEmpty: boolean): Job {
    return {
      id: newId(), file, name: file.name, srcDur: 0, a: 0, b: 0, work, slot: id, photo: isPicture(file), mustBeEmpty,
      state: 'wait', pct: 0, error: '', thumb: '', abort: null, row: null
    };
  }
  /** many files into the empty slots, in order (CapCut-style); files beyond the empty slots are named, not dropped */
  async function fillSlots(files: File[]) {
    const p = ctx.project(); if (!p) return;
    if (ctx.readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อน', true); return; }
    const other = otherWork(); if (other) { toast(other, true); return; }
    const waiting = new Set(jobs.filter(j => j.slot && (j.state === 'wait' || j.state === 'run')).map(j => j.slot));
    const empty = p.clips.filter(c => isEmptySlot(c) && !waiting.has(c.id)).map(c => c.id);
    if (!empty.length) { toast('ไม่มีช่องว่างแล้ว ถ้าจะเปลี่ยนคลิปของช่องไหน กด "เปลี่ยน" ที่ช่องนั้น', true); return; }
    const use = files.slice(0, empty.length), work = ctx.saver.openedCount;
    queue(use.map((f, k) => slotJob(f, empty[k], work, true)));
    if (files.length > empty.length) {
      toast(`เลือกมา ${files.length} ไฟล์ แต่มีช่องว่าง ${empty.length} ช่อง จึงใช้ ${empty.length} ไฟล์แรก (${files.slice(empty.length).map(f => shortText(f.name, 20)).join(', ')} ไม่ได้ใช้)`, true);
    }
  }
  /** one file for slot `id`: a video opens step 2 with the frame as long as the slot; a picture goes straight in */
  async function fillOne(file: File, id: string) {
    if (ctx.readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อน', true); return; }
    const other = otherWork(); if (other) { toast(other, true); return; }
    if (jobs.some(j => j.slot === id && (j.state === 'wait' || j.state === 'run'))) { toast('ช่องนี้กำลังเตรียมคลิปอยู่ รอให้เสร็จก่อน', true); return; }
    if (isPicture(file)) { queue([slotJob(file, id, ctx.saver.openedCount, false)]); return; }
    await choose(file, { id, len: slotLength(id) });
  }

  /* ---------- 3. preparing, one clip at a time ---------- */
  let jobs: Job[] = [];
  let running = false;
  let wake: { release: () => Promise<void> } | null = null;

  function makeRow(j: Job) {
    const li = document.createElement('li'); li.className = 'vpitem';
    const img: HTMLElement = j.thumb ? Object.assign(document.createElement('img'), { src: j.thumb, alt: '' }) : Object.assign(document.createElement('span'), { className: 'vpthumb' });
    const body = document.createElement('div');
    const name = document.createElement('b'); name.textContent = j.name;
    const sub = document.createElement('span'); sub.className = 'muted';
    const bar = document.createElement('div'); bar.className = 'vbar'; bar.append(document.createElement('i'));
    const err = document.createElement('span'); err.className = 'err-text'; err.hidden = true;
    const acts = document.createElement('div'); acts.className = 'addrow'; acts.hidden = true;
    const retry = document.createElement('button'); retry.className = 'btn'; retry.textContent = 'ลองอีกครั้ง';
    retry.onclick = () => { j.state = 'wait'; j.error = ''; j.pct = 0; update(j); void runQueue(); };
    const stopBtn = document.createElement('button'); stopBtn.className = 'btn'; stopBtn.textContent = 'หยุด';
    stopBtn.onclick = () => j.abort?.abort();
    const remove = document.createElement('button'); remove.className = 'btn'; remove.textContent = 'เอาออก';
    remove.onclick = () => {
      if (j.state === 'run') return;
      jobs = jobs.filter(x => x !== j);
      setTimeout(() => { dropRow(j); renderChip(); renderTitle(); }, 0);   // not under the finger that tapped it (iPad)
    };
    acts.append(retry, stopBtn, remove);
    body.append(name, sub, bar, err, acts);
    const right = document.createElement('span');
    li.append(img, body, right);
    j.row = { li, img, sub, right, bar, err, acts, retry, stopBtn, remove };
    list.append(li);
  }
  function dropRow(j: Job) { j.row?.li.remove(); j.row = null; }

  function update(j: Job) {
    if (!j.row) makeRow(j);
    const r = j.row!;
    const len = j.b - j.a;
    const where = j.slot ? (() => { const k = ctx.project()?.clips.findIndex(c => c.id === j.slot) ?? -1; return k >= 0 ? `ช่องที่ ${k + 1} · ` : ''; })() : '';
    r.sub.textContent = j.photo ? `${where}รูปนิ่ง` + (j.state === 'wait' ? ' · รอคิว' : '') : `${where}ช่วง ${fmtTime(j.a)}–${fmtTime(j.b)} · ${len.toFixed(1)} วิ` + (j.state === 'run' ? ` · เหลือราว ${Math.max(1, Math.round(len * PREP_SECONDS_PER_SECOND * (1 - j.pct)))} วิ` : j.state === 'wait' ? ' · รอคิว' : '');
    (r.bar.firstElementChild as HTMLElement).style.width = `${Math.round(j.pct * 100)}%`;
    r.bar.hidden = j.state !== 'run' && j.state !== 'wait';
    r.err.hidden = j.state !== 'error'; r.err.textContent = j.error;
    // running: หยุด · waiting: เอาออก · failed: ลองอีกครั้ง + เอาออก (buttons are shown/hidden, never rebuilt)
    r.acts.hidden = j.state === 'done';
    r.retry.hidden = j.state !== 'error';
    r.stopBtn.hidden = j.state !== 'run';
    r.remove.hidden = j.state === 'run';
    r.right.className = j.state === 'done' ? 'vpok' : 'vppct';
    r.right.textContent = j.state === 'done' ? 'พร้อม' : j.state === 'run' ? `${Math.round(j.pct * 100)}%` : '';
  }

  function renderList() { jobs.forEach(update); renderTitle(); renderChip(); }
  function renderTitle() {
    const done = jobs.filter(j => j.state === 'done').length;
    const failed = jobs.filter(j => j.state === 'error').length;
    $('vpTitle').textContent = !jobs.length ? 'เตรียมคลิป'
      : jobs.every(j => j.state === 'done') ? 'เตรียมคลิปเสร็จแล้ว'
      : `กำลังเตรียมคลิป · ${done} จาก ${jobs.length} เสร็จแล้ว${failed ? ` · ไม่สำเร็จ ${failed}` : ''}`;
  }
  function renderChip() {
    const run = jobs.find(j => j.state === 'run');
    const waiting = jobs.filter(j => j.state === 'wait' || j.state === 'run').length;
    const failed = jobs.filter(j => j.state === 'error').length;
    chip.hidden = !waiting && !failed;
    chip.textContent = waiting ? `กำลังเตรียม ${waiting} คลิป${run ? ` · ${Math.round(run.pct * 100)}%` : ''}` : `เตรียมไม่สำเร็จ ${failed} คลิป`;
    chip.classList.toggle('err-text', !waiting && !!failed);
  }
  chip.onclick = () => openSheet('shVPrep');

  async function lockScreen(on: boolean) {
    try {
      if (on && !wake) wake = await (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<{ release: () => Promise<void> }> } }).wakeLock?.request('screen') ?? null;
      if (!on && wake) { await wake.release(); wake = null; }
    } catch { wake = null; }   // not available: the warning in the list asks to keep the screen on
  }

  async function runQueue() {
    if (running) return;
    running = true;
    await lockScreen(true);
    try {
      for (let j = jobs.find(x => x.state === 'wait'); j; j = jobs.find(x => x.state === 'wait')) await runJob(j);
    } finally {
      running = false;
      await lockScreen(false);
      renderChip(); renderTitle();
    }
  }

  async function runJob(j: Job) {
    j.state = 'run'; j.pct = 0; j.abort = new AbortController();
    update(j); renderChip(); renderTitle();
    let key = '';
    try {
      // a slot filled from "many files" may have been filled another way meanwhile: never put over it unasked
      const slotNow = j.slot ? ctx.project()?.clips.find(c => c.id === j.slot) : null;
      if (j.slot && j.mustBeEmpty && ctx.saver.openedCount === j.work && slotNow && !isEmptySlot(slotNow)) {
        throw new Error('ช่องนี้ถูกเติมไปแล้วระหว่างรอคิว จึงไม่ใส่ทับ ถ้าต้องการใช้ไฟล์นี้ กด "เปลี่ยน" ที่ช่องนั้น');
      }
      let clip: VideoClip;
      if (j.photo) {
        const pic = await preparePhoto(j.file, j.name);
        j.pct = 0.9; update(j);
        key = newId();
        try { await putMedia(key, pic.blob, { kind: 'photo', name: j.name, duration: 0, thumb: pic.thumb }); }
        catch (e) { key = ''; throw new Error('เก็บรูปในเครื่องไม่สำเร็จ: ' + mediaErrorText(e)); }
        clip = {
          id: newId(), media: key, name: shortText(j.name, NAME_MAX_CHARS), srcDur: PHOTO_MAX_SECONDS, srcStart: 0, srcEnd: PHOTO_MAX_SECONDS,
          copyDur: PHOTO_MAX_SECONDS, in: 0, out: 3, volume: 1, hasAudio: false, fade: false, kind: 'photo', hold: 0, slot: null
        };
      } else {
        if (j.slot && j.b <= j.a) {   // a file that could not be read at first ("ลองอีกครั้ง"): read it again
          const info = await (await videoConvert()).probe(j.file, j.name);
          j.srcDur = info.duration; j.a = 0; j.b = Math.min(info.duration, slotLength(j.slot));
        }
        const copy = await (await videoConvert()).makeCopy(j.file, j.name, j.a, j.b, p => { j.pct = p; update(j); renderChip(); }, j.abort.signal);
        key = newId();
        try { await putMedia(key, copy.blob, { kind: 'clip', name: j.name, duration: copy.duration, thumb: copy.thumb }); }
        catch (e) { key = ''; throw new Error('เก็บคลิปในเครื่องไม่สำเร็จ: ' + mediaErrorText(e)); }
        clip = {
          id: newId(), media: key, name: shortText(j.name, NAME_MAX_CHARS), srcDur: j.srcDur, srcStart: j.a, srcEnd: j.b, copyDur: copy.duration,
          in: 0, out: copy.duration, volume: 1, hasAudio: copy.hasAudio, fade: false, kind: 'video', hold: 0, slot: null
        };
      }
      if (ctx.saver.openedCount !== j.work || !ctx.project()) {
        throw new Error('งานวิดีโอที่เลือกคลิปนี้ไม่ได้เปิดอยู่แล้ว จึงยังไม่ได้ใส่คลิปลงไป เปิดงานนั้นแล้วนำเข้าคลิปใหม่');
      }
      if (j.slot) {
        const p = ctx.project()!;
        const k = p.clips.findIndex(c => c.id === j.slot);
        if (k < 0) throw new Error('ช่องที่เลือกไว้ไม่มีแล้ว (อาจถูกลบหรือเลิกทำไป) จึงยังไม่ได้ใส่');
        if (!ctx.commit(fillSlot(p, k, clip))) throw new Error('ใส่ลงช่องไม่ได้ (ดูเหตุผลที่แจ้งไว้)');
        j.state = 'done'; j.pct = 1;
        const c = ctx.project()!.clips[k];
        toast(`ใส่ "${shortText(j.name, 30)}" ลงช่องที่ ${k + 1}${c.slot ? ` (${c.slot.label})` : ''} แล้ว${c.hold > 0.05 ? ` · สั้นกว่าช่อง ภาพสุดท้ายค้าง ${c.hold.toFixed(1)} วิ` : ''}`);
      } else {
        if (j.photo) throw new Error('รูปนิ่งใส่ได้เฉพาะในช่องของเทมเพลต');
        const ok = ctx.commit(addClip(ctx.project()!, clip));
        if (!ok) throw new Error('ใส่คลิปลงไทม์ไลน์ไม่ได้ (ดูเหตุผลที่แจ้งไว้)');
        j.state = 'done'; j.pct = 1;
        toast(`เตรียม "${shortText(j.name, 40)}" เสร็จแล้ว ใส่ไว้ท้ายไทม์ไลน์`);
      }
    } catch (e) {
      j.state = 'error'; j.error = errText(e);
      if (key) void deleteMedia(key).catch(() => {});    // the copy is not used by any clip
      if (!isOpen('shVPrep')) toast(j.error, true);
    } finally {
      j.abort = null;
      update(j); renderChip(); renderTitle();
    }
  }
  /** a clip is being prepared: opening another work now would leave it without a place to go */
  function busy(): string | null {
    return jobs.some(j => j.state === 'run' || j.state === 'wait') ? 'กำลังเตรียมคลิปอยู่ รอให้เสร็จก่อน (ดูที่ปุ่ม "กำลังเตรียม" บนหน้าวิดีโอ)' : null;
  }

  return { busy, closePick, fillSlots, fillOne };
}
